import React from 'react';
import ConnectionStatus from '@/constants/ConnectionStatus';
import IWaveConnectBroadcastChannelMessageType from '@/constants/integrations/IWaveConnectBroadcastChannelMessageType';
import useBroadcastChannel from '@/hooks/ui/useBroadcastChannel';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import deleteIWaveCredentials from '@/services/integrations/deleteIWaveCredentials';
import type IWaveConnectBroadcastChannelMessage from '@/types/IWaveConnectBroadcastChannelMessage';
import IWaveConnectBroadcastChannelName from '../constants/IWaveConnectBroadcastChannelName';
import useIWaveIntegration from './useIWaveIntegration';

export default function useIWaveConnectIntegration() {
  /**
   * Track the connection status separate from the actual API data. This
   * will allow for an optimistic update when deleting the connection.
   */
  const [connectionStatus, setConnectionStatus] =
    React.useState<null | ConnectionStatus>(null);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const iWaveIntegration = useIWaveIntegration();
  const ownerOrgId = useOwnerOrgId();
  const { message, sendMessage } =
    useBroadcastChannel<IWaveConnectBroadcastChannelMessage>(
      IWaveConnectBroadcastChannelName,
    );

  /**
   * Set the connection status when the first API request is completed.
   * After that, the BroadcastChannel and will handle setting the status
   * to "connected", and the DELETE API request will set it to "disconnected".
   */
  if (iWaveIntegration?.status && connectionStatus === null) {
    setConnectionStatus(iWaveIntegration.status);
  }

  React.useEffect(() => {
    if (message?.type === IWaveConnectBroadcastChannelMessageType.CONNECTED) {
      setConnectionStatus(ConnectionStatus.CONNECTED);
      sendMessage({
        type: IWaveConnectBroadcastChannelMessageType.CLOSE_AUTH_WINDOW,
      });
    }
  }, [message, sendMessage]);

  const disconnectIWave = React.useCallback(async () => {
    setIsDisconnecting(true);
    try {
      await deleteIWaveCredentials(ownerOrgId);
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
    } finally {
      setIsDisconnecting(false);
    }
  }, [ownerOrgId]);

  return {
    connectionStatus,
    disconnectIWave,
    isLoading: !iWaveIntegration,
    isDisconnecting,
  };
}
